import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styles from './GiftingHeroBrandRefresh.module.css';
import {
  GIFTING_HERO_DEFAULT,
  GIFTING_HERO_HEADLINE_LINES_MOBILE,
  GIFTING_HERO_MARQUEE_ITEMS,
  GIFTING_HERO_MOBILE_LAYOUT_MAX_WIDTH,
} from './giftingHeroConstants';
import {
  getCardExtras,
  getResponsiveHeroImageUrl,
  selectGiftingHeroContentCard,
} from './giftingHeroContentCardHelpers';
import elementConstant from '../../../../shared/constants/element';
import * as contentCardConstants from '../../../../shared/constants/contentCards';
import { routeCountryPath } from '../../../../shared/utilities/common';
import { trackContentCardClick, trackContentCardImpression } from '../../../../shared/utilities/brazeTracking';
import * as brazeActions from '../../../../redux/actions/braze';

const heroCookieBox = require('./imgs/hero-cookie-box.png');
const heroCookieCrumbs = require('./imgs/hero-cookie-crumbs.png');
const heroShadow = require('./imgs/hero-shadow.svg');
const heroSticker = require('./imgs/hero-sticker.svg');
const iconCookie = require('./imgs/icon-cookie.svg');
const iconDelivery = require('./imgs/icon-delivery.svg');
const iconGift = require('./imgs/icon-gift.svg');

const MARQUEE_ICONS = {
  cookie: iconCookie,
  delivery: iconDelivery,
  gift: iconGift,
};

function GiftingHeroMarquee() {
  const group = (
    <div className={styles.marqueeGroup} aria-hidden="true">
      {GIFTING_HERO_MARQUEE_ITEMS.map((item) => (
        <div key={item.id} className={styles.marqueeItem}>
          <img
            src={MARQUEE_ICONS[item.iconKey]}
            alt=""
            className={styles.marqueeIcon}
          />
          <span className={styles.marqueeLabel}>{item.label}</span>
        </div>
      ))}
    </div>
  );

  return (
    <div className={styles.marqueeBar} data-part="gifting-hero-marquee">
      <div
        className={styles.marqueeViewport}
        aria-label="Gifting highlights"
      >
        <div className={styles.marqueeTrack}>
          {group}
          {group}
          {group}
        </div>
      </div>
    </div>
  );
}

/** Shadow, crumbs, main image (Braze URL or default PNG), and animated sticker. */
function HeroArtComposite({ mainImageSrc }) {
  return (
    <>
      <div className={styles.artLayerShadow} aria-hidden="true">
        <img src={heroShadow} alt="" />
      </div>
      <img
        src={heroCookieCrumbs}
        alt=""
        className={styles.artLayerCrumbsSecondary}
        aria-hidden="true"
      />
      <img
        src={heroCookieCrumbs}
        alt=""
        className={styles.artLayerCrumbsPrimary}
        aria-hidden="true"
      />
      <img
        src={mainImageSrc || heroCookieBox}
        alt=""
        className={styles.artLayerMain}
        data-part="gifting-hero-main-image"
      />
      <div className={styles.artStickerWrap} aria-hidden="true">
        <img src={heroSticker} alt="" className={styles.artSticker} />
      </div>
    </>
  );
}

HeroArtComposite.propTypes = {
  mainImageSrc: PropTypes.string,
};

HeroArtComposite.defaultProps = {
  mainImageSrc: null,
};

export function GiftingHeroBrandRefresh({
  heroCard,
  headlineLines,
  preheader,
  body,
  ctaText,
  ctaLink,
  ctaButtonType,
  contentSection,
  onCtaClick,
  contentCardSession,
}) {
  const history = useHistory();
  const [isNarrow, setIsNarrow] = useState(
    typeof window !== 'undefined'
      && window.innerWidth <= elementConstant.introHeroBannerWidthThreshold
  );
  const [isMobileLayout, setIsMobileLayout] = useState(
    typeof window !== 'undefined'
      && window.innerWidth <= GIFTING_HERO_MOBILE_LAYOUT_MAX_WIDTH
  );

  useEffect(() => {
    const onResize = () => {
      setIsNarrow(window.innerWidth <= elementConstant.introHeroBannerWidthThreshold);
      setIsMobileLayout(window.innerWidth <= GIFTING_HERO_MOBILE_LAYOUT_MAX_WIDTH);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (heroCard) {
      trackContentCardImpression(heroCard);
    }
  }, [heroCard]);

  const extras = useMemo(() => getCardExtras(heroCard), [heroCard]);
  const brazeHeroImageUrl = useMemo(
    () => getResponsiveHeroImageUrl(extras, isNarrow),
    [extras, isNarrow]
  );

  const brazeHeadline = heroCard?.title;
  const displayHeadlineLines = isMobileLayout && !brazeHeadline
    ? GIFTING_HERO_HEADLINE_LINES_MOBILE
    : headlineLines;
  const displayPreheader = extras.preheaderText || preheader;
  const displayBody = heroCard?.description || body;
  const displayCtaText = extras.actionButtonText || ctaText;
  const cardUrl = heroCard?.url || heroCard?.u;

  const navigateCard = useCallback(() => {
    if (!heroCard || !cardUrl) {
      return;
    }
    trackContentCardClick(heroCard);
    contentCardSession(true);

    if (cardUrl.startsWith(contentCardConstants.EXTERNAL_URL_PREFIX)) {
      window.open(cardUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    history.push(routeCountryPath(cardUrl));
  }, [heroCard, cardUrl, contentCardSession, history]);

  const handleCtaClick = () => {
    if (extras.actionButtonText && cardUrl) {
      navigateCard();
      return;
    }
    if (onCtaClick && ctaLink) {
      onCtaClick(ctaLink, ctaButtonType, displayCtaText, contentSection);
    }
  };

  const ctaStyle =
    extras.actionButtonFill || extras.actionButtonTextColor
      ? {
        backgroundColor: extras.actionButtonFill || '#efff00',
        color: extras.actionButtonTextColor || '#3b0055',
      }
      : undefined;

  return (
    <section
      className={styles.section}
      data-region="gifting-hero"
      aria-labelledby="gifting-hero-heading"
    >
      <div className={styles.heroStage}>
        <div className={styles.heroInner}>
          <div className={styles.copyBlock} data-part="gifting-hero-copy">
            {brazeHeadline ? (
              <h1 id="gifting-hero-heading" className={styles.headlineBraze}>
                {brazeHeadline}
              </h1>
            ) : (
              <h1 id="gifting-hero-heading" className={styles.headline}>
                {displayHeadlineLines.map((line) => (
                  <span key={line} className={styles.headlineLine}>
                    {line}
                  </span>
                ))}
              </h1>
            )}
            <div className={styles.actionRow}>
              <div className={styles.subcopyBlock}>
                {displayPreheader ? (
                  <p className={styles.preheader}>{displayPreheader}</p>
                ) : null}
                {displayBody ? (
                  <p className={styles.body}>{displayBody}</p>
                ) : null}
              </div>
              <button
                type="button"
                className={styles.ctaPrimary}
                data-part="gifting-hero-cta"
                onClick={handleCtaClick}
                style={ctaStyle}
              >
                {displayCtaText}
              </button>
            </div>
          </div>
          <div className={styles.art} data-part="gifting-hero-media">
            <HeroArtComposite mainImageSrc={brazeHeroImageUrl} />
          </div>
        </div>
      </div>
      <GiftingHeroMarquee />
    </section>
  );
}

GiftingHeroBrandRefresh.propTypes = {
  heroCard: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    url: PropTypes.string,
    u: PropTypes.string,
  }),
  headlineLines: PropTypes.arrayOf(PropTypes.string),
  preheader: PropTypes.string,
  body: PropTypes.string,
  ctaText: PropTypes.string,
  ctaLink: PropTypes.string,
  ctaButtonType: PropTypes.string,
  contentSection: PropTypes.string,
  onCtaClick: PropTypes.func,
  contentCardSession: PropTypes.func.isRequired,
};

GiftingHeroBrandRefresh.defaultProps = {
  ...GIFTING_HERO_DEFAULT,
  heroCard: null,
  onCtaClick: null,
};

export function mapStateToProps(state) {
  const { selectedStore } = state.user;
  const {
    contentCards,
    brazeContentCardLink,
    brazeContentCardLinkReplaceWith,
  } = state.braze;

  const currentUrl =
    typeof window !== 'undefined' ? window.location.href : null;

  const heroCard = selectGiftingHeroContentCard(
    contentCards,
    selectedStore,
    currentUrl,
    brazeContentCardLink,
    brazeContentCardLinkReplaceWith
  );

  return { heroCard };
}

export const mapDispatchToProps = dispatch => ({
  contentCardSession: value => dispatch(brazeActions.contentCardSession(value)),
});

export default connect(mapStateToProps, mapDispatchToProps)(GiftingHeroBrandRefresh);
